// src/components/ui/Sheet.tsx — SCE Issues
// Panneau responsive : bottom sheet sur mobile, modale centrée sur bureau.
//
// ─── Pourquoi un composant plutôt que du style inline ───
//
// Le basculement repose sur `@media (min-width: 768px)`, et un style inline
// React ne peut pas porter de media query. Les règles vivent donc dans
// globals.css (.sheet-overlay / .sheet-panel) — même approche qu'Audit5S.
//
// ─── Comportement ───
//
//   < 768px : feuille collée en bas, coins arrondis en haut, 85vh max
//   ≥ 768px : boîte centrée sur fond sombre, 640px max, ombre portée
//
// Fermeture : clic sur l'overlay, touche Échap, ou bouton × de l'en-tête.

import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useClicFond } from '@/hooks/useClicFond'

export interface SheetProps {
  titre: string
  onClose: () => void
  children: ReactNode
  /** Barre d'actions collée en bas du panneau (boutons Appliquer/Réinitialiser…). */
  footer?: ReactNode
}

export function Sheet({ titre, onClose, children, footer }: SheetProps): JSX.Element {
  // Échap ferme le panneau — attendu sur bureau, où il n'y a pas de geste
  // de balayage pour s'en débarrasser.
  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [onClose])

  // Le fond ne ferme que si le clic y a commencé (sélection de texte glissée hors du cadre).
  const fond = useClicFond(onClose)
  return (
    <div
      className="sheet-overlay"
      {...fond}
      role="presentation"
    >
      <div
        className="sheet-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
      >
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--color-border)', flexShrink: 0 }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-copy1)',
              lineHeight: 'var(--line-height-copy1)',
              fontWeight: 500,
              margin: 0,
            }}
          >
            {titre}
          </h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="p-1">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">{children}</div>

        {footer && (
          <div
            className="flex gap-3 p-4 border-t"
            style={{
              borderColor: 'var(--color-border)',
              paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0))',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
